import React, { useEffect, useState } from "react";
import { CardContent } from "@/components/ui/card";
import "./widgets.css";
import { useDashboardData } from "@/services/dashboardDataService";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import { cleanDateAndTimeObject } from "@shared/renderDate";

interface IncompleteUsersData {
  usersWithIncompleteReadings: number;
  totalUsers: number;
  percentage: number;
  lastUpdated?: string | { _seconds: number; _nanoseconds: number } | Date;
  error?: boolean;
  errorMessage?: string;
}

const UsersWithIncompleteReadingCountWidget: React.FC = () => {
  const { data, isLoading, error, refetch } = useDashboardData('users-with-incomplete-readings');
  const [usersWithIncomplete, setUsersWithIncomplete] = useState<number>(0);
  const [lastUpdated, setLastUpdated] = useState<string|Date>('');
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [percentage, setPercentage] = useState<number>(0);

  // Report widget status to dashboard context
  useWidgetStatusReporter('users-with-incomplete-readings', isLoading, error, !!data);

  useEffect(() => {
    if (data) {
      const incompleteData = data as IncompleteUsersData;
      setUsersWithIncomplete(incompleteData.usersWithIncompleteReadings || 0);
      setTotalUsers(incompleteData.totalUsers || 0);
      setPercentage(incompleteData.percentage || 0);
      
      if (incompleteData.lastUpdated) {
        setLastUpdated(cleanDateAndTimeObject(incompleteData.lastUpdated));
      }
    }
  }, [data]);

  if (isLoading) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
        <h3 className="text-lg font-semibold mb-2">Users with Incomplete Reading</h3>
        <div className="text-muted-foreground">Loading...</div>
      </CardContent>
    );
  }

  if (error || data?.error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center p-4" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">Users with Incomplete Reading</h3>
        <div className="text-destructive text-center">
          {data?.errorMessage || 'Error loading data'}
          <button 
            onClick={() => refetch()} 
            className="mt-2 text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      </CardContent>
    );
  }

  // Determine the color based on the percentage of users with incomplete readings
  const textColor = percentage > 50 ? 'text-red-600' : 'text-primary';
  const isSingular = usersWithIncomplete === 1;

  return (
    <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <h3 className="text-lg font-semibold mb-2">Users with Incomplete Reading</h3>
      <div
        className="flex flex-col items-center justify-center flex-1 w-full h-full"
        style={{ gap: 'clamp(0.5rem, 2cqw, 2rem)' }}
      >
        <span
          className={`font-bold ${textColor}`}
          style={{
            fontSize: 'clamp(2.5rem, 8cqw, 7rem)',
            lineHeight: 1,
            minHeight: '1em',
          }}
        >
          {usersWithIncomplete}
        </span>
        <span
          className="text-muted-foreground text-center"
          style={{
            fontSize: 'clamp(1rem, 2.5cqw, 2rem)',
          }}
        >
          {isSingular ? 'user has' : 'users have'} incomplete reading
          {totalUsers > 0 && (
            <span className="block text-sm mt-1">
              ({percentage}% of {totalUsers} {totalUsers === 1 ? 'user' : 'users'})
            </span>
          )}
        </span>
        {lastUpdated && (
          <span 
            className="text-xs text-muted-foreground mt-2"
            title={`Last updated: ${lastUpdated}`}
          >
            Updated: {new Date(lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
    </CardContent>
  );
};

export default React.memo(UsersWithIncompleteReadingCountWidget);
